import { assign } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';

import Colors from '../../constants/Colors';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine
} from '../charts/Victory';

/*
  data - Values for the Graph
  position - User current position in the data
  xAxisMinValueLabel - Minimum x-axis domain value
  xAxisMaxValueLabel - Maximum x-axis domain value
  yAxisMaxValueLabel - Maximum y-axis domain value
*/
export type CompareChartProps = {
  intervention: string;
  subject: string;
  study: string;
  data: any;
  position: number;
  xAxisMinValueLabel: number;
  xAxisMaxValueLabel: number;
  yAxisMaxValueLabel: number;
  xAxisValueUnits?: string;
  xAxisBottomLabel?: string;
  yAxisLeftLabel?: string;
  plugin?: boolean;
};

function getSignedNumber(value: number) {
  if (value > 0) {
    return '+' + value;
  } else {
    return value.toString();
  }
}

const CompareChart: React.FC<CompareChartProps> = ({
  intervention,
  subject,
  study,
  data,
  position,
  xAxisMinValueLabel,
  xAxisMaxValueLabel,
  yAxisMaxValueLabel,
  xAxisValueUnits = '',
  yAxisLeftLabel = '% OF STUDY PARTICIPANTS',
  xAxisBottomLabel = `IMPROVEMENT IN ${study.toUpperCase()}`,
  plugin
}) => {
  const window = useWindowDimensions();
  const isMobile = window.width <= 739;
  const isTablet = window.width >= 740 && window.width <= 1024;
  const isDesktop = window.width >= 768 && window.width < 1200;
  const isLargeDesktop = window.width >= 1200;
  const xOffsets = [49.1, window.width * (isDesktop ? 0.69 : 0.9) - 49.1];
  const tickPadding = [-3, -22];
  const anchors = ['end', 'start'];
  const [xAxisMinData, setXAxisMinData] = useState(0);
  const [xAxisWebMinData, setxAxisWebMinData] = useState(0);
  const [xAxisMaxData, setXAxisMaxData] = useState(0);
  const [xAxisCustomMinData, setXAxisCustomMinData] = useState(0);
  const [xAxisCustomMaxData, setXAxisCustomMaxData] = useState(0);

  useEffect(() => {
    getXData(data);
  }, [data]);

  // Retrieve x-axis datas
  const getXData = (datas: any) => {
    datas.map((item: { x: number }, index: number) => {
      index === 1 && setXAxisCustomMinData(item.x);
      index === 2 && setxAxisWebMinData(item.x);
      index === 5 && setXAxisMinData(item.x);
      index === datas.length - 6 && setXAxisMaxData(item.x);
      index === datas.length - 3 && setXAxisCustomMaxData(item.x);
    });
  };

  // Dynamic positioning of X-axis Red Line label
  const xAxisLabelPosition = () => {
    if (isLargeDesktop) {
      if (position <= xAxisCustomMinData - 0.1) {
        return manipulateXAxisLabelPosition(true, -5);
      } else if (position > xAxisCustomMaxData + 0.2) {
        return manipulateXAxisLabelPosition(false, -6);
      } else {
        return 0;
      }
    } else if (isDesktop) {
      if (position <= xAxisWebMinData) {
        return manipulateXAxisLabelPosition(true, -5);
      } else if (position >= xAxisCustomMaxData) {
        return manipulateXAxisLabelPosition(false, -6);
      } else {
        return 0;
      }
    } else {
      if (position <= xAxisMinData) {
        return manipulateXAxisLabelPosition(true, -10);
      } else if (position >= xAxisMaxData) {
        return manipulateXAxisLabelPosition(false, -10);
      } else {
        return 0;
      }
    }
  };

  // Computation for X-axis Red Line label
  const manipulateXAxisLabelPosition = (
    isNegativeSign: boolean,
    addedValue: number
  ) => {
    if (position === xAxisMinValueLabel || position === xAxisMaxValueLabel) {
      return isNegativeSign ? 39 : -39;
    } else {
      const positionSet = Math.round((position % 1) * 100) / 100;
      if (Math.abs(positionSet) > 0.5) {
        return (-30 + addedValue) * positionSet;
      } else {
        return Math.abs(positionSet) === 0
          ? (-40 + addedValue) *
              (isNegativeSign ? positionSet - 0.04 : positionSet + 0.04)
          : (-40 + addedValue) * positionSet;
      }
    }
  };

  // Dynamic x-axis domain range handling
  const xAxisDomainRange = () => {
    if (window.width < 376) {
      return manipulateDomainRange(-1, 1, xAxisMinData, xAxisMaxData);
    } else {
      if (isLargeDesktop) {
        return manipulateDomainRange(
          -0.3,
          0.3,
          xAxisWebMinData,
          xAxisCustomMaxData + 0.2
        );
      } else if (isDesktop) {
        return manipulateDomainRange(
          -0.5,
          0.5,
          xAxisWebMinData,
          xAxisCustomMaxData
        );
      } else {
        return manipulateDomainRange(
          Platform.OS === 'web' ? -0.6 : -0.8,
          Platform.OS === 'web' ? 0.6 : 0.7,
          xAxisWebMinData + (Platform.OS === 'web' ? 0.2 : 0.3),
          xAxisMaxData + 0.3
        );
      }
    }
  };

  // Setup for x-axis domain range
  const manipulateDomainRange = (
    posMinMiddle: number,
    posMaxMiddle: number,
    posMin: number,
    posMax: number
  ) => {
    if (position < posMaxMiddle && position > posMinMiddle) {
      return [xAxisMinValueLabel, position, xAxisMaxValueLabel];
    } else if (position > posMax) {
      return [xAxisMinValueLabel, 0, position];
    } else if (position < posMin) {
      return [0, position, xAxisMaxValueLabel];
    } else {
      return [xAxisMinValueLabel, 0, position, xAxisMaxValueLabel];
    }
  };

  const positionLine = [
    { x: position, y: 0 },
    {
      x: position,
      y: yAxisMaxValueLabel - yAxisMaxValueLabel * 0.09,
      label: 'You are here'
    }
  ];

  const verticalGuideLine = [
    { x: 0, y: 0 },
    { x: 0, y: yAxisMaxValueLabel }
  ];

  const getChartElementStyles = (index: number) => {
    const xAxisTickStyle = {
      ticks: { padding: 1 },
      tickLabels: {
        fill:
          index === -1
            ? 'transparent'
            : plugin
            ? Colors.chloeBlack
            : Colors.primaryPurple,
        fontSize: '10px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '11.73px',
        letterSpacing: '-0.49px'
      }
    };

    const yAxisTickStyle = {
      ticks: { padding: tickPadding[index] },
      tickLabels: {
        fill: plugin ? Colors.chloeBlack : Colors.primaryPurple,
        fontSize: '10px',
        fontFamily: 'WorkSans-Regular',
        lineHeight: '11.73px',
        letterSpacing: '-0.49px',
        textAnchor: anchors[index]
      }
    };

    const xAxisStyle = assign({}, xAxisTickStyle, {
      grid: {
        stroke: plugin ? '#27475C' : Colors.xAxisChartGuideColor,
        strokeWidth: 0.5
      },
      axis: {
        stroke: plugin ? Colors.chloeBlack : Colors.primaryPurple,
        strokeWidth: 1.2
      },
      axisLabel: {
        fill: plugin ? Colors.chloeBlack : Colors.primaryPurple,
        fontSize: '12px',
        fontFamily: 'WorkSans-Medium',
        lineHeight: '14.08px',
        letterSpacing: '-0.49px',
        padding: 50
      }
    });

    const yAxisStyle = assign({}, yAxisTickStyle, {
      axis: {
        stroke: plugin ? Colors.compareChartStrokeSolid : Colors.primaryPurple,
        strokeWidth: 1.2
      },
      axisLabel: {
        fill: plugin ? Colors.chloeBlack : Colors.primaryPurple,
        fontSize: '10px',
        fontFamily: 'WorkSans-Regular',
        lineHeight: '11.73px',
        letterSpacing: '-0.49px',
        padding: 30
      }
    });

    const yAxis2ndStyle = {
      axis: {
        stroke: plugin ? Colors.compareChartStrokeSolid : Colors.primaryPurple,
        strokeWidth: 1.2
      },
      tickLabels: {
        fill: 'transparent',
        textAnchor: anchors[index]
      }
    };

    const titleLabelStyle = {
      fill: Colors.primaryPurple,
      fontSize: '16px',
      fontFamily: 'WorkSans-SemiBold',
      lineHeight: '83.85px',
      letterSpacing: '-0.65px'
    };

    const labelStyle = {
      fill: 'white',
      fontSize: '10px',
      fontFamily: 'WorkSans-Regular',
      letterSpacing: '-0.49px'
    };

    const guideLineStyle = {
      data: {
        stroke: Colors.compareChartGuideLineColor,
        strokeWidth: 1,
        strokeDasharray: '3, 1'
      }
    };

    const positionLineStyle = {
      data: {
        stroke: plugin ? '#2E90A8' : Colors.compareChartPositionColor,
        strokeWidth: 3
      }
    };

    return {
      titleLabel: titleLabelStyle,
      labels: labelStyle,
      xAxis: xAxisStyle,
      yAxis: yAxisStyle,
      yAxisSecond: yAxis2ndStyle,
      guideLine: guideLineStyle,
      position: positionLineStyle
    };
  };

  return (
    <VictoryChart
      padding={{
        top: 35,
        bottom: Platform.OS === 'web' ? 61.5 : 69,
        left: 50,
        right: 50
      }}
      width={
        window.width * (isDesktop ? 1 : isMobile || isTablet ? 0.99 : 0.77)
      }
      height={250}
      domain={{ y: [0, yAxisMaxValueLabel] }}
    >
      {/* Chart Title */}
      {!plugin && (
        <VictoryLabel
          key={0}
          text={`Does ${intervention} affect your ${subject.toLowerCase()}?`}
          x={
            (window.width *
              (isDesktop ? 1 : isMobile || isTablet ? 0.99 : 0.77)) /
            2
          }
          y={window.width < 375 ? 15 : 6}
          textAnchor='middle'
          style={getChartElementStyles(-1).titleLabel}
        />
      )}

      {/* Y-Axis */}
      <VictoryAxis
        key={1}
        crossAxis={false}
        label={yAxisLeftLabel}
        dependentAxis
        offsetX={xOffsets[0]}
        orientation='left'
        style={getChartElementStyles(0).yAxis}
      />

      <VictoryAxis
        key={2}
        dependentAxis
        offsetX={49}
        orientation='right'
        style={getChartElementStyles(1).yAxisSecond}
      />

      {/* X-Axis */}
      <VictoryAxis
        key={3}
        crossAxis={false}
        label={xAxisBottomLabel}
        offsetY={Platform.OS === 'web' ? 61 : 68.5}
        style={getChartElementStyles(0).xAxis}
        tickValues={[
          xAxisMinValueLabel,
          +(
            xAxisMinValueLabel +
            0.4 * (xAxisMaxValueLabel - xAxisMinValueLabel)
          ).toFixed(1),
          +(
            xAxisMinValueLabel +
            0.64 * (xAxisMaxValueLabel - xAxisMinValueLabel)
          ).toFixed(1),
          xAxisMaxValueLabel
        ]}
        tickFormat={(tickLabel: number) =>
          `${getSignedNumber(
            tickLabel
          )}\n${xAxisValueUnits.toUpperCase()}`.trim()
        }
        fixLabelOverlap
      />

      <VictoryAxis
        key={4}
        offsetY={Platform.OS === 'web' ? 61 : 68.5}
        style={getChartElementStyles(-1).xAxis}
        tickCount={data.length}
      />

      {/* Graph */}
      <VictoryArea
        key={5}
        interpolation='basis'
        style={{
          data: {
            fill: plugin ? '#CCF5FF' : Colors.compareChartAreaColor
          }
        }}
        data={data}
      />

      {/* Position Lines */}
      <VictoryLine
        key={7}
        data={positionLine}
        style={getChartElementStyles(-1).position}
        labels={(data: { label: any }) => data.label}
        labelComponent={
          <VictoryLabel
            dx={xAxisLabelPosition()}
            dy={13}
            lineHeight={2}
            style={getChartElementStyles(-1).labels}
            backgroundStyle={{
              fill: plugin ? '#2E90A8' : Colors.compareChartPositionColor,
              rx: 9
            }}
            backgroundPadding={{
              top: -2.8,
              bottom: -2.8,
              left: 2,
              right: 2
            }}
          />
        }
      />
    </VictoryChart>
  );
};

export default CompareChart;
